// @owners { team: patient-health }
import { BORDERS, COLORS, SPACING } from '@alto/design-library-tokens';
import { ActionSheetContext, ActionSheetV2, Body, Button, Column, LgPadding, Row } from '@alto/design-system';
import { faMortarPestle } from '@fortawesome/pro-light-svg-icons/faMortarPestle';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import React, { useCallback, useContext, useEffect } from 'react';
import styledNative from 'styled-components/native';
import { getIsVideosEnabled } from '~shared/features/instructional-videos/selectors/getIsVideosEnabled';
import { getInstructionalVideosForMedication } from '~shared/features/my-meds/helper';
import { getPartitionedPrescriptionsForMedication } from '~shared/features/my-meds/selectors/getPartitionedPrescriptionsByMedication';
import { type Medication } from '~shared/features/my-meds/types';
import { isActiveRx } from '~shared/features/prescriptions/helper';
import { getURLParams, replaceState } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useMedListDetailsViewed } from '~shared/lib/analytics/src/hooks/useMedListDetailsViewed';
import { useSelectorShared } from '~shared/store';
import { type Prescription as PrescriptionType } from '~shared/types/clients';
import { LearnMore } from './LearnMore';
import { AttrIcon } from './MedListDetailMedAttr';
import MedListInstructionalVideos from './MedListInstructionalVideosContainer';
import Prescription from './PrescriptionContainer';
import InfoRow from '~web/components/InfoRow';
import AncillaryItemsTag from '~web/features/my-meds/components/AncillaryItemsTag';
import RefrigerationRequiredTag from '~web/features/my-meds/components/RefrigerationRequiredTag';

const MedListDetailContainer = styledNative(Column)`
  cursor: default;
  padding: 0;
`;

const MedListDetailRow = styledNative(Row)`
  background-color:  ${COLORS.BACKGROUND_COLORS.WHITE};
  border: ${BORDERS.SIZE}px solid ${COLORS.BORDER_COLORS.LIGHT};
  border-radius: ${BORDERS.RADIUS.XS.px};
  padding: ${SPACING.STATIC.MD.rem};
`;

const MedListDetailVideosRow = styledNative(MedListDetailRow)`
  padding: 0;
`;

type ViewInactivePrescriptionsButtonProps = {
  readonly inactivePrescriptions: PrescriptionType[];
  readonly medicationName: string;
};

const ViewInactivePrescriptionsButton = ({
  inactivePrescriptions,
  medicationName,
}: ViewInactivePrescriptionsButtonProps) => {
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const handleViewPastPrescriptions = useCallback(() => {
    setActiveActionSheet(
      <ActionSheetV2
        title={`${inactivePrescriptions.length} Inactive Prescriptions for ${medicationName}`}
        buttons={[
          <Button
            key="done-button"
            type="primary"
            onPress={closeActionSheet}
          >
            Done
          </Button>,
        ]}
      >
        <LgPadding
          topPadding={SPACING.STATIC.NONE}
          bottomPadding={SPACING.STATIC.NONE}
        >
          {inactivePrescriptions.map((prescription) => (
            <Prescription
              key={prescription.rx_number}
              prescription={prescription}
            />
          ))}
        </LgPadding>
      </ActionSheetV2>,
    );
  }, [closeActionSheet, inactivePrescriptions, medicationName, setActiveActionSheet]);

  return (
    <Column>
      <Button
        type="secondary"
        onPress={handleViewPastPrescriptions}
      >
        {`View Past Prescriptions (${inactivePrescriptions.length})`}
      </Button>
    </Column>
  );
};

type LearnMoreButtonProps = {
  readonly medicationName: string;
  readonly monographProductID: number;
  readonly prescriptionId: number;
};

const LearnMoreButton = ({ medicationName, monographProductID, prescriptionId }: LearnMoreButtonProps) => {
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  const handleViewLearnMore = useCallback(() => {
    setActiveActionSheet(
      <ActionSheetV2
        title={medicationName}
        buttons={[
          <Button
            key="done-button"
            type="primary"
            onPress={closeActionSheet}
          >
            Done
          </Button>,
        ]}
      >
        <LgPadding
          topPadding={SPACING.STATIC.NONE}
          bottomPadding={SPACING.STATIC.NONE}
        >
          <LearnMore productID={monographProductID} />
        </LgPadding>
      </ActionSheetV2>,
    );

    trackEvent({
      event: EVENTS.MED_DETAILS__LEARN_MORE_VIEWED,
      params: { medication_name: medicationName, product_id: monographProductID },
      additionalFields: { prescriptionId },
    });
  }, [closeActionSheet, medicationName, monographProductID, prescriptionId, setActiveActionSheet, trackEvent]);

  return (
    <Button
      type="tertiary"
      iconLeftName="info"
      noPadding
      small
      onPress={handleViewLearnMore}
    >
      {`Learn more about ${medicationName}`}
    </Button>
  );
};

type Props = {
  readonly medication: Medication;
  readonly suppressAction: boolean;
};

export const MedListDetail = ({ medication, suppressAction }: Props) => {
  const { origin } = getURLParams();
  const includeFertVideos = useSelectorShared(getIsVideosEnabled);
  const { brands, medicationName, monographProductID, prescriptions, refrigerated } = medication;
  const { actionablePrescriptions, inactivePrescriptions } = useSelectorShared((state) =>
    getPartitionedPrescriptionsForMedication(state, { medication }),
  );
  const activePrescriptions = actionablePrescriptions.filter(isActiveRx);
  const hasAncillaryItems = prescriptions?.some?.((rx) => rx.has_ancillary_items) ?? false;
  const instructionalVideos = getInstructionalVideosForMedication(medication);
  const { createMedListDetailsViewedAnalyticsEvent } = useMedListDetailsViewed({
    medication,
    actionablePrescriptionsCount: actionablePrescriptions.length,
    inactivePrescriptionsCount: inactivePrescriptions.length,
  });

  useEffect(() => {
    createMedListDetailsViewedAnalyticsEvent(origin || ORIGIN_NAMES.MED_LIST);
    if (origin) {
      replaceState('/prescriptions');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MedListDetailContainer gap="MD">
      {activePrescriptions.length === 0 && (
        <MedListDetailRow>
          <Row centerVertically>
            <AttrIcon
              color={COLORS.PALETTE.DANGER.DEFAULT}
              icon={faExclamationTriangle}
            />
            <Body fontFamily="bold">You have no active prescriptions.</Body>
          </Row>
        </MedListDetailRow>
      )}
      {actionablePrescriptions.map((prescription) => (
        <Prescription
          isSinglePrescription={actionablePrescriptions.length === 1}
          key={prescription.rx_number}
          prescription={prescription}
          suppressAction={activePrescriptions.length === 1 ? suppressAction : false}
        />
      ))}
      {inactivePrescriptions.length > 0 ? (
        <ViewInactivePrescriptionsButton
          inactivePrescriptions={inactivePrescriptions}
          medicationName={medicationName}
        />
      ) : null}
      {brands.length > 0 || refrigerated || hasAncillaryItems ? (
        <MedListDetailRow>
          <Column>
            {brands.length > 0 ? <InfoRow icon={faMortarPestle}>{`Generic for ${brands.join(', ')}`}</InfoRow> : null}
            {refrigerated ? <RefrigerationRequiredTag medicationName={medicationName} /> : null}
            {hasAncillaryItems ? <AncillaryItemsTag medicationName={medicationName} /> : null}
          </Column>
        </MedListDetailRow>
      ) : null}
      {monographProductID ? (
        <LearnMoreButton
          medicationName={medicationName}
          monographProductID={monographProductID}
          prescriptionId={medication.representativePrescription.id}
        />
      ) : null}
      {includeFertVideos && !!instructionalVideos && !!instructionalVideos.length ? (
        <MedListDetailVideosRow>
          <MedListInstructionalVideos videos={instructionalVideos} />
        </MedListDetailVideosRow>
      ) : null}
    </MedListDetailContainer>
  );
};
